import { gql } from '@apollo/client';
import { Routes } from '../navigator/types';
import { CurrencySymbol, Locale } from '@buy-viasat/utils';

export enum Modals {
  ADDRESS_NOT_VERIFIED = 'addressNotVerified',
  BACK_BUTTON_PROMPT = 'backButtonPrompt',
  BILLING_ADDRESS_NOT_VERIFIED = 'billingAddressNotVerified',
  SHIPPING_ADDRESS_NOT_VERIFIED = 'shippingAddressNotVerified',
  CAE = 'cae',
  CONFIRM_SERVICE_ADDRESS = 'confirmServiceAddress',
  CONFIRM_BILLING_ADDRESS = 'confirmBillingAddress',
  CONFIRM_SHIPPING_ADDRESS = 'confirmShippingAddress',
  GET_PLANS_SERVER_ERROR = 'getPlansServerError',
  NO_ADDRESS_FOUND = 'noAddressFound',
  NO_AVAILABLE_PLANS = 'noAvailablePlans',
  NO_SERVICE = 'noService',
  ERROR = 'error',
  AUTH_ERROR = 'authError',
  DEFAULT = 'default',
  UPDATE_BILLING_ADDRESS = 'updateBillingAddress',
  PAYMENT_INFORMATION_ERROR = 'paymentInformationError',
  SCHEDULE_LATER = 'scheduleLater',
  REMOVE_OPTIONAL_SERVICE = 'removeOptionalService',
  UPSERT_WITH_CLOSING_OFFER_RETRY_ERROR = 'upsertWithClosingOfferRetryError',
  CLOSING_OFFER_NOT_ELIGIBLE = 'closingOfferNotEligible',
}

export const CAFII = 'CAF II';

// We need to keep this enum in sync with the backend where B stands for Business and R stands for Residential
// This is used to determine the customer type in the application
// and is used in various places like feature flags, queries, etc.
export enum CustomerType {
  R = 'r',
  B = 'b',
}

export enum Country {
  US = 'US',
}

export const initialAppState: AppState = {
  errorRetryAttempts: 0,
  errorRetryAction: null,
  billingContactEqualsAccountHolder: true,
  onSiteContactEqualsAccountHolder: true,
  creditCheckResults: null,
  currentModal: Modals.CAE,
  currentRoute: Routes.SERVICEABILITY,
  locale: Locale.EN_US,
  currencySymbol: CurrencySymbol.US,
  customerType: CustomerType.R,
  country: Country.US,
  isAvailable: true,
  isCartVisible: false,
  isCheckoutButtonDisabled: false,
  isModalVisible: false,
  isOrderSubmitting: false,
  isPaymentInformationComplete: false,
  isPersonalInformationComplete: false,
  isScheduleInstallationComplete: false,
  isTaxCodesLoading: true,
  location: undefined,
  maxRoute: Routes.SERVICEABILITY,
  partyId: '',
  organizationPartyId: '',
  orderId: '',
  relnId: '',
  redirected: false,
  serviceabilityError: false,
  serviceabilityMaybe: false,
  startTime: new Date().toString(),
  loadedFeatureFlags: false,
  featureFlagsUserKey: undefined,
  featureFlags: {
    maintenanceMode: false,
    enableFiber: false,
    enableUnleashedMax: false,
    enableCAFContent: false,
  },
  loadedCidPhoneMap: false,
  cidPhoneNumber: '',
  cidPhoneNumberCampaignId: undefined,
  urlParams: {},
  workOrderId: '',
  hasSubmittedScheduleInstall: false,
  salesAgreementId: '',
  fulfillmentAgreementId: '',
  errorModalOverrideHeader: undefined,
  errorModalOverrideBody: undefined,
  errorModalOverrideButtonText: undefined,
  errorModalOverrideSvg: undefined,
  smbLinkResidentialRedirect: false,
  smbLinkPlansRedirect: false,
  isEmbedded: false,
  isStandAlone: false,
  timer: 0,
  hideMfeCTA: false,
  displayCalculatedLaterCartLabel: false,
};

export type AppState = {
  errorRetryAttempts: number;
  errorRetryAction: { type: string; payload: any } | null;
  billingContactEqualsAccountHolder: boolean;
  onSiteContactEqualsAccountHolder: boolean;
  creditCheckResults: CreditCheck[] | null;
  currentModal: Modals;
  currentRoute: Routes;
  locale: string;
  currencySymbol: CurrencySymbol;
  customerType: CustomerType;
  country: Country;
  isAvailable: boolean;
  isCartVisible: boolean;
  isCheckoutButtonDisabled: boolean;
  isModalVisible: boolean;
  isOrderSubmitting: boolean;
  isPaymentInformationComplete: boolean;
  isPersonalInformationComplete: boolean;
  isScheduleInstallationComplete: boolean;
  isTaxCodesLoading: boolean;
  location: string | undefined;
  maxRoute: Routes;
  partyId: string;
  organizationPartyId: string;
  orderId: string;
  relnId: string;
  redirected: boolean;
  serviceabilityError: boolean;
  serviceabilityMaybe: boolean;
  startTime: string;
  loadedFeatureFlags: boolean;
  featureFlagsUserKey: string | undefined;
  featureFlags: Features;
  loadedCidPhoneMap: boolean;
  cidPhoneNumber: string;
  // The campaignId that cidPhoneNumber was resolved for. Used to detect when a newly-captured
  // campaignId differs from the one already loaded, so the Navigator effect knows to re-fetch
  // instead of relying solely on loadedCidPhoneMap (which would otherwise never refetch again
  // for the rest of the session, even if a different campaignId shows up).
  cidPhoneNumberCampaignId: string | undefined;
  urlParams: QueryParams;
  workOrderId: string;
  hasSubmittedScheduleInstall: boolean;
  salesAgreementId: string;
  fulfillmentAgreementId: string;
  errorModalOverrideHeader: string | undefined;
  errorModalOverrideBody: string | undefined;
  errorModalOverrideButtonText: string | undefined;
  errorModalOverrideSvg: string | undefined;
  smbLinkResidentialRedirect: boolean;
  smbLinkPlansRedirect: boolean;
  isStandAlone: boolean;
  isEmbedded: boolean;
  timer: number;
  hideMfeCTA: boolean;
  displayCalculatedLaterCartLabel: boolean;
};

export type Features = {
  maintenanceMode: boolean;
  enableFiber: boolean;
  enableUnleashedMax: boolean;
  enableCAFContent: boolean;
};

export enum CreditTag {
  DEFAULT = 'CREDIT_TAG',
}

export type CreditCheck = {
  name: CreditTag;
  value: string;
};

export enum ScriptId {
  ADOBE_LAUNCH = 'adobeLaunch',
  GOOGLE_PLACES = 'googlePlaces',
  ADOBE_TARGET = 'adobeTarget',
}

export type QueryParams = {
  dealerId?: string;
  partnerId?: string;
  affiliateId?: string;
  sessionId?: string;
  campaignId?: string;
  hideCta?: string;
  offerId?: string;
  plnId?: string;
};

export type ErrorModalOverride = {
  overrideHeader?: string;
  overrideBody?: string;
  overrideButtonText?: string;
  overrideSvg?: string;
  modal: Modals;
};

export const FEATURE_FLAGS = gql`
  query getFeatureFlags {
    getFeatureFlags {
      maintenanceMode
      enableFiber
      enableUnleashedMax
      enableCAFContent
    }
  }
`;

export enum ThunkActions {
  getFeatureFlags = 'getFeatureFlags',
}
